import { Location } from '@angular/common';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { DOCUMENT, inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '@env/environment';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { AuthService } from 'app/core/services/auth/auth.service';
import { CustomMsalService } from 'app/core/services/auth/custom-msal.service';
import { catchError, EMPTY, from, Observable, switchMap } from 'rxjs';
import { AUTHENTICATION_CONFIGURATION_NAMES } from '../services/auth/authenticationConfigNames';
import { ConfigService } from '../services/config/config.service';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor
  extends MsalInterceptor
  implements HttpInterceptor
{
  private readonly _oktaAuth = inject<OktaAuth>(OKTA_AUTH, { optional: true });
  private readonly configService = inject(ConfigService);
  private customMsalService = inject(CustomMsalService);
  private router = inject(Router);
  private injector = inject(Injector);

  constructor() {
    const msalInterceptorConfig = inject<MsalInterceptorConfiguration>(
      MSAL_INTERCEPTOR_CONFIG,
      { optional: true },
    );
    const document = inject<Document>(DOCUMENT);
    const location = inject(Location);
    const msalService = inject(MsalService, { optional: true });
    const msalBroadcastService = inject(MsalBroadcastService, {
      optional: true,
    });

    super(
      msalInterceptorConfig,
      msalService,
      location,
      msalBroadcastService,
      document,
    );
  }

  /**
   * MSAL cannot renew the session silently (refresh token expired and
   * the hidden-iframe fallback needs third-party cookies the browser
   * blocks). Sending the request without a token would just produce a
   * 401, so abort it and send the user to the login page to
   * re-authenticate interactively.
   */
  private redirectToInteractiveLogin(): Observable<never> {
    // AuthService is resolved lazily to avoid a DI cycle
    // (interceptor -> AuthService -> HttpClient -> interceptor).
    this.injector.get(AuthService).clearLocalAuthState();
    this.router.navigate(['/login'], {
      queryParams: { returnUrl: this.router.url },
    });
    return EMPTY;
  }

  override intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    let req = request;

    if (
      this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA] &&
      localStorage.getItem('esqep.authType') ===
        AUTHENTICATION_CONFIGURATION_NAMES.OKTA
    ) {
      return next.handle(this.addAuthHeaderToAllowedOrigins(request));
    }
    if (
      this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.CSOD] &&
      localStorage.getItem('esqep.authType') ===
        AUTHENTICATION_CONFIGURATION_NAMES.CSOD
    ) {
      const authToken = localStorage.getItem('esqep.auth.saml');

      if (authToken) {
        req = request.clone({
          setHeaders: {
            Authorization: `Bearer ${authToken}`,
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        });
      }
      return next.handle(req);
    }

    if (
      (this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.ENTRA] &&
        localStorage.getItem('esqep.authType') ===
          AUTHENTICATION_CONFIGURATION_NAMES.ENTRA) ||
      (this.configService.authSetup[AUTHENTICATION_CONFIGURATION_NAMES.B2C] &&
        localStorage.getItem('esqep.authType') ===
          AUTHENTICATION_CONFIGURATION_NAMES.B2C)
    ) {
      const isB2C =
        localStorage.getItem('esqep.authType') ===
        AUTHENTICATION_CONFIGURATION_NAMES.B2C;

      if (
        isB2C &&
        (request.url.includes(environment.serverUrl) ||
          request.url.includes(':7044'))
      ) {
        const account =
          this.customMsalService.instance.getActiveAccount() ||
          this.customMsalService.instance.getAllAccounts()[0];

        if (account) {
          const b2cConfig =
            this.configService.authSetup[
              AUTHENTICATION_CONFIGURATION_NAMES.B2C
            ];
          const b2cAuthority = `https://${b2cConfig.authorityDomain}/${b2cConfig.tenantId}`;
          return from(
            this.customMsalService.instance.acquireTokenSilent({
              scopes: [b2cConfig.clientId + '/.default'],
              account: account,
              authority: b2cAuthority,
            }),
          ).pipe(
            switchMap((result) => {
              const authReq = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${result.accessToken}`,
                },
              });
              return next.handle(authReq);
            }),
            catchError((err) => {
              if (err instanceof InteractionRequiredAuthError) {
                console.error(
                  'B2C CIAM session requires interactive login:',
                  err,
                );
                return this.redirectToInteractiveLogin();
              }
              if (err.status === 401 || err.status === 403) {
                // Handle unauthorized errors (token acquisition failure)
                // console.error('Unauthorized B2C CIAM (token) error:', err);
              } else if (err.status === 500) {
                // Handle internal server errors from backend
                // console.error('Internal Server Error:', err);
              } else {
                // other errors
                // console.error('An unhandled error occurred:', err);
              }
              // for other errors, the interceptor will pass the original request on to the backend without the token
              // and it will fail with a 401 in order to ensure the pipeline continues
              // some refactoring may be needed here in the future to avoid unnecessary calls to the backend without a token
              return next.handle(request);
            }),
          );
        } else {
          // console.warn(
          //   'B2C Interceptor: No active account found for request',
          //   request.url,
          // );
        }
      } else if (
        !isB2C &&
        (request.url.includes(environment.serverUrl) ||
          request.url.includes(':7044'))
      ) {
        const account =
          this.customMsalService.instance.getActiveAccount() ||
          this.customMsalService.instance.getAllAccounts()[0];
        if (account) {
          const entraConfig =
            this.configService.authSetup[
              AUTHENTICATION_CONFIGURATION_NAMES.ENTRA
            ];
          return from(
            this.customMsalService.instance.acquireTokenSilent({
              scopes: [entraConfig.clientId + '/.default'],
              account: account,
              authority: entraConfig.authority,
            }),
          ).pipe(
            switchMap((result) => {
              const authReq = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${result.accessToken}`,
                },
              });
              return next.handle(authReq);
            }),
            catchError((err) => {
              if (err instanceof InteractionRequiredAuthError) {
                console.error('ENTRA session requires interactive login:', err);
                return this.redirectToInteractiveLogin();
              }
              if (err.status === 401 || err.status === 403) {
                // Handle unauthorized errors (token acquisition failure)
                // console.error('Unauthorized ENTRA (token) error:', err);
              } else if (err.status === 500) {
                // Handle internal server errors from backend
                // console.error('Internal Server Error:', err);
              } else {
                // other errors
                // console.error('An unhandled error occurred:', err);
              }
              // for other errors, the interceptor will pass the original request on to the backend without the token
              // and it will fail with a 401 in order to ensure the pipeline continues
              // some refactoring may be needed here in the future to avoid unnecessary calls to the backend without a token
              return next.handle(request);
            }),
          );
        }
      }

      req = request.clone({
        setHeaders: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      return next.handle(req);
    }
    return next.handle(req);
  }

  private addAuthHeaderToAllowedOrigins(
    request: HttpRequest<unknown>,
  ): HttpRequest<unknown> {
    let req = request;
    if (
      !!this.configService.authSetup[
        AUTHENTICATION_CONFIGURATION_NAMES.OKTA
      ].allowedOrigins.find((origin) => request.url.includes(origin))
    ) {
      const authToken = this._oktaAuth.getAccessToken();
      req = request.clone({
        setHeaders: {
          Authorization: `Bearer ${authToken}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
    }

    return req;
  }
}
